import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import Header from "./components/ui/Header";
import ProtectedRoute from "components/ProtectedRoute";
import { APP_ROUTES } from "utils/appRoutes";
import AppLayout from "layout/AppLayout";

// Lazy imports
const UserLogin = lazy(() => import("./pages/user-login"));
const TemplateLibrary = lazy(() => import("./pages/template-library"));
const Dashboard = lazy(() => import("./pages/dashboard"));
const UserRegistration = lazy(() => import("./pages/user-registration"));
const ProposalEditor = lazy(() => import("./pages/proposal-editor"));
const RFPUploadAndParsing = lazy(
  () => import("./pages/rfp-upload-and-parsing"),
);
const GroupOrganizationManagement = lazy(
  () => import("pages/group-organization-management"),
);
const PersonnelProfileManagement = lazy(
  () => import("./pages/personnel-profile-management"),
);
const PreviousRFPArchive = lazy(() => import("./pages/previous-rfp-archive"));
const ContentRecyclingAssistant = lazy(
  () => import("./pages/content-recycling-assistant"),
);
const OrgUploadDocs = lazy(() => import("pages/org-upload-docs/Index"));
const MasterManagement = lazy(
  () => import("pages/masters-management/MasterManagement"),
);
const AssignedRequirements = lazy(() => import("pages/assigned-requirements"));
const ChangePasswordPage = lazy(
  () => import("pages/user-login/components/ChangePasswordPage"),
);
const Pricing = lazy(() => import("pages/Pricing/index"));

const AssignedRFP = lazy(() => import("pages/assigned-rfp/index"));
const VerifyOTP = lazy(() => import("pages/user-login/components/VerifyOTP"));

const EnableMFA = lazy(() => import("pages/security/EnableMFA"));
const OrgSelection = lazy(() => import("pages/org-selection"));

function Routes() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />

        <Suspense fallback={null}>
          <RouterRoutes>
            <Route path={APP_ROUTES.LOGIN} element={<UserLogin />} />
            <Route path="/user-login" element={<UserLogin />} />
            <Route path="/verify-otp" element={<VerifyOTP />} />

            <Route path="/security/mfa" element={<EnableMFA />} />

            <Route path="/security/mfa-reset" element={<EnableMFA />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/org-selection" element={<OrgSelection />} />

            <Route element={<AppLayout />}>
              <Route
                path={APP_ROUTES.TEMPLATE_LIBRARY}
                element={
                  <ProtectedRoute>
                    <TemplateLibrary />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.DASHBOARD}
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path={APP_ROUTES.USER_REGISTRATION}
                element={<UserRegistration />}
              />

              <Route
                path={APP_ROUTES.PROPOSAL_EDITOR}
                element={
                  <ProtectedRoute>
                    <ProposalEditor />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.RFP_UPLOAD}
                element={
                  <ProtectedRoute>
                    <RFPUploadAndParsing />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.ORG_MANAGEMENT}
                element={
                  <ProtectedRoute>
                    <GroupOrganizationManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.UPLOAD_ORG_DOCS}
                element={
                  <ProtectedRoute>
                    <OrgUploadDocs />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.PERSONNEL_MANAGEMENT}
                element={
                  <ProtectedRoute>
                    <PersonnelProfileManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.PREVIOUS_RFP}
                element={
                  <ProtectedRoute>
                    <PreviousRFPArchive />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.CONTENT_RECYCLING}
                element={
                  <ProtectedRoute>
                    <ContentRecyclingAssistant />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.MASTERS_MANAGEMENT}
                element={
                  <ProtectedRoute>
                    <MasterManagement />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.ASSIGNED_REQUIREMENTS}
                element={
                  <ProtectedRoute>
                    <AssignedRequirements />
                  </ProtectedRoute>
                }
              />

              <Route
                path={APP_ROUTES.ASSIGNED_RFP}
                element={
                  <ProtectedRoute>
                    <AssignedRFP />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path={APP_ROUTES.PRICING} element={<Pricing />} />

            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default Routes;
